// pwa/src/components/LocationModalContent.js
import React, { useState, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import DynamicSelect from "./DynamicSelect";
import QRCodeGenerator from "./QRCodeGenerator";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

const LocationModalContent = React.forwardRef(
  ({ shopId, productId, onSaveSuccess }, ref) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const textFieldRef = useRef(null);
    const [hashId, setHashId] = useState("");
    const [formData, setFormData] = useState({
      shopId: shopId,
      floorId: "",
      name: "",
      description: "",
      active: false,
    });

    const qrLink = process.env.REACT_APP_FRONT_URL + "/qr/" + hashId;

    React.useImperativeHandle(ref, () => ({
      handleClickOpen,
      handleClose,
    }));

    // Console log para debugging de shopId (si se necesita)
    useEffect(() => {
      // Si necesitas hacer algo con shopId independientemente de productId, hazlo aquí.
      console.log("shopId in ModalContent:", shopId);
    }, [shopId]);

    // MODIFICADO useEffect: Maneja la carga de datos para edición y el reseteo para creación, basado en productId
    useEffect(() => {
      // Asegúrate de resetear shopId en formData cuando shopId cambie
      setFormData((prev) => ({ ...prev, shopId: shopId }));

      if (productId) {
        // Modo edición: Cargar datos del producto
        console.log(`Fetching location data for ID: ${productId}`);
        // Asegúrate de que esta URL de la API sea correcta para obtener un producto por ID
        axios
          .get(process.env.REACT_APP_API_URL + `/api/locations/${productId}`)
          .then((response) => {
            const locationData = response.data; // Renombrado de productData a locationData para claridad
            setHashId(locationData.hashId || "");
            setFormData({
              shopId: shopId, // Asegura que shopId se mantenga
              floorId: locationData.floorId || "",
              name: locationData.name || "",
              description: locationData.description || "",
              // CORREGIDO: Convertir 'active' a booleano al cargar desde la API
              active: !!locationData.active, // Converts 0/1 or null/undefined to false/true
            });
            console.log("Location data loaded:", locationData);
          })
          .catch((error) => {
            console.error(
              `Error fetching location data for ID ${productId}:`,
              error
            );
            // Opcional: mostrar un mensaje de error al usuario
            // Si hay un error al cargar, reseteamos el formulario como si fuera creación
            setFormData({
              shopId: shopId,
              floorId: "",
              name: "",
              description: "",
              active: false, // CORREGIDO: Resetear a false
            });
          });
      } else {
        // Modo creación: Reiniciar el formulario
        console.log("Modal opened for new location creation. Resetting form.");
        setFormData({
          shopId: shopId,
          floorId: "",
          name: "",
          description: "",
          active: false, // CORREGIDO: Resetear a false
        });
      }
    }, [productId, shopId]); // Ejecutar este efecto cada vez que productId o shopId cambien

    const handleClickOpen = () => {
      setOpen(true);
      // Ya no reiniciamos formData aquí. El useEffect lo hará basado en `productId`.
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleChange = (event) => {
      const { name, value, type, checked } = event.target; // Destructurar 'type' y 'checked'
      setFormData((prevFormData) => ({
        ...prevFormData,
        // CORREGIDO: Manejar checkboxes correctamente usando 'checked'
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleSubmit = (event) => {
      event.preventDefault();

      // CORREGIDO: Convertir el booleano 'active' a 1 o 0 para la base de datos
      const dataToSend = {
        ...formData,
        active: formData.active ? 1 : 0,
      };

      console.log("Submitting form data:", dataToSend);

      const apiEndpoint = productId
        ? process.env.REACT_APP_API_URL + `/api/locations/${productId}` // Para actualización
        : process.env.REACT_APP_API_URL + "/api/locations"; // Para creación

      const httpMethod = productId ? axios.put : axios.post; // Usar PUT para actualizar, POST para crear

      httpMethod(apiEndpoint, dataToSend) // Usar dataToSend
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            // 200 para PUT/OK, 201 para POST/Created
            console.log("Location operation successful:", response.data);
            handleClose();
            // AÑADIDO: Llamar a la función de callback si se proporciona
            if (onSaveSuccess) {
              onSaveSuccess();
            }
          } else {
            console.log("API response status not successful:", response.status);
          }
        })
        .catch((error) => {
          console.log("handleSubmit location error: ");
          console.error(error);
        });
    };

    const handleFloorChange = (newValue) => {
      console.log("Nuevo floor seleccionado:", newValue);
      setFormData((prev) => ({ ...prev, floorId: newValue }));
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        TransitionProps={{
          onEntered: () => {
            if (textFieldRef.current) {
              textFieldRef.current.focus();
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {/* Título dinámico: "Edit Product" o "Create Product" */}
            <FormattedMessage
              id={
                productId
                  ? "app.header.location.label"
                  : "app.header.location.label"
              }
            />
          </span>
          <Box>
            <Tooltip title="Close">
              <IconButton aria-label="Close" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form style={{ marginTop: "10px" }} onSubmit={handleSubmit}>
            <Grid
              container
              direction={"column"}
              spacing={2}
              className={"settingsContainer"}
            >
              <Grid item>
                <DynamicSelect
                  apiModel="floors"
                  componentLabel="Plantas"
                  onItemChange={handleFloorChange}
                  defaultValue={formData.floorId}
                />
              </Grid>
              <Grid item>
                <TextField
                  name="name"
                  id="outlined-basic-title"
                  label="Titulo"
                  fullWidth
                  value={formData.name}
                  onChange={handleChange}
                  inputRef={textFieldRef}
                />
              </Grid>
              <Grid item>
                <TextField
                  name="description"
                  id="outlined-multiline-static-description"
                  label="Descripcion"
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="active"
                      id="outlined-basic-active"
                      // CORREGIDO: Usar 'checked' en lugar de 'defaultChecked' y 'value'
                      checked={formData.active}
                      onChange={handleChange}
                    />
                  }
                  label="Activo"
                />
              </Grid>
              <Grid item>
                <QRCodeGenerator url={qrLink} />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default LocationModalContent;
